import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Zap, 
  Shield, 
  CheckCircle,
  Phone,
  Award,
  Clock,
  MapPin,
  Home,
  Building,
  Factory,
  AlertTriangle,
  Lightbulb,
  Settings,
  Star,
  Users,
  Mail
} from "lucide-react";

// Import electrical images
import electricianImage from "@/assets/electrician-ontario.jpg";
import electricianPanelWork from "@/assets/electrician-panel-work.jpg";
import electricianLighting from "@/assets/electrician-lighting.jpg";
import commercialElectrical from "@/assets/commercial-electrical.jpg";
import emergencyElectrical from "@/assets/emergency-electrical.jpg";

const ElectricianServices = () => {
  const faqData = [
    {
      question: "Do you provide emergency electrical services in Toronto?",
      answer: "Yes. Blue Knight Contracting provides 24/7 emergency electrical services throughout Toronto and the GTA for power outages, electrical faults, and urgent safety issues."
    },
    {
      question: "Are your electricians licensed in Ontario?",
      answer: "Absolutely. All our electricians are fully licensed by Electrical Safety Authority (ESA) in Ontario, with extensive experience in residential, commercial, and industrial electrical work."
    },
    {
      question: "Do you handle electrical panel upgrades?",
      answer: "Yes. We specialize in electrical panel upgrades and replacements, bringing older systems up to current Ontario electrical codes for improved safety and capacity."
    },
    {
      question: "Can you provide electrical services for commercial buildings?",
      answer: "Yes. Our licensed electricians handle all types of commercial electrical work including office buildings, retail spaces, restaurants, warehouses, and industrial facilities across Ontario."
    },
    {
      question: "Do you offer electrical inspections and maintenance?",
      answer: "Yes. We provide comprehensive electrical inspections, preventive maintenance, and electrical safety assessments to ensure your systems are safe and code-compliant."
    }
  ];

  const serviceData = {
    "@type": "Service",
    name: "Electrician Services Ontario",
    description: "Professional licensed electrician services across Ontario including electrical installation, repair, panel upgrades, and emergency services for residential and commercial properties.",
    provider: {
      "@type": "Organization",
      name: "Blue Knight Contracting and Construction"
    },
    serviceType: "Electrical Services",
    areaServed: "Ontario, Canada"
  };

  const electricalServices = [
    {
      title: "Residential Electrical",
      description: "Complete electrical services for homes including wiring, panels, outlets, lighting, and safety inspections.",
      icon: Home,
      services: [
        "Electrical panel upgrades",
        "Home rewiring",
        "Outlet and switch installation",
        "Ceiling fan installation",
        "GFCI and AFCI protection",
        "Electrical troubleshooting"
      ]
    },
    {
      title: "Commercial Electrical",
      description: "Professional electrical solutions for offices, retail, restaurants, and commercial facilities.",
      icon: Building,
      services: [
        "Office electrical systems",
        "Retail lighting design",
        "Power distribution",
        "Emergency lighting",
        "Security system wiring",
        "Energy-efficient upgrades"
      ]
    },
    {
      title: "Industrial Electrical",
      description: "Heavy-duty electrical work for manufacturing, warehouses, and industrial facilities.",
      icon: Factory,
      services: [
        "Motor control systems",
        "High-voltage installations",
        "Automation wiring",
        "Safety shut-off systems",
        "Equipment electrical connections",
        "Electrical maintenance programs"
      ]
    },
    {
      title: "Emergency Services",
      description: "24/7 emergency electrical repairs for power outages, electrical faults, and safety hazards.",
      icon: AlertTriangle,
      services: [
        "Power outage troubleshooting",
        "Electrical fault repairs",
        "Emergency panel repairs",
        "Safety hazard resolution",
        "Storm damage electrical work",
        "After-hours service calls"
      ]
    },
    {
      title: "Lighting Solutions",
      description: "Interior and exterior lighting design, installation, and LED retrofit services.",
      icon: Lightbulb,
      services: [
        "LED lighting retrofits",
        "Landscape lighting",
        "Security lighting",
        "Smart lighting systems",
        "Decorative lighting",
        "Energy-efficient solutions"
      ]
    },
    {
      title: "Electrical Upgrades",
      description: "Panel upgrades, code compliance updates, and electrical system modernization.",
      icon: Settings,
      services: [
        "Panel upgrades and replacements",
        "Code compliance updates",
        "Electrical system modernization",
        "Capacity upgrades",
        "Safety improvements",
        "Smart home integration"
      ]
    }
  ];

  const serviceAreas = [
    "Greater Toronto Area",
    "Toronto", "Mississauga", "Brampton", "Vaughan", "Markham", "Richmond Hill", "Oakville", "Burlington", "Milton",
    "Niagara Region", 
    "Niagara Falls", "St. Catharines", "Welland", "Niagara-on-the-Lake", "Fort Erie", "Grimsby", "Lincoln", "Pelham", "Port Colborne", "Thorold",
    "Eastern Ontario",
    "Ottawa", "Kingston", "Cornwall", "Belleville", "Pembroke", "Petawawa",
    "Southwestern Ontario", 
    "London", "Windsor", "Kitchener", "Waterloo", "Cambridge", "Guelph", "Sarnia", "Chatham",
    "Northern Ontario",
    "Sudbury", "Thunder Bay", "North Bay", "Sault Ste. Marie", "Timmins", "Kenora"
  ];

  return (
    <>
      <SEOMeta 
        title="Electrical Contractor — Repairs, Wiring & Lighting | Niagara • Hamilton • GTA"
        description="Certified electricians for wiring, panel upgrades and lighting across Niagara, Hamilton & the GTA. Reliable residential and commercial service."
        keywords="electrician Niagara, Hamilton electrical contractor, GTA electrician services, rewiring, panel upgrade, lighting install, commercial electrical"
        canonicalUrl="https://blueknightcontracting.ca/services/electrical"
      />
      <SchemaMarkup 
        type="Service"
        data={{
          ...serviceData,
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: faqData.map(faq => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer
              }
            }))
          }
        }}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-primary/80 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                  Licensed Electricians in Ontario
                </h1>
                <p className="text-xl mb-8 opacity-90">
                  Blue Knight Contracting provides professional electrical services across Ontario with licensed, experienced electricians. From residential repairs to commercial installations and emergency services, we deliver safe, code-compliant electrical work.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="secondary">
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                    <Phone className="h-5 w-5 mr-2" />
                    24/7 Emergency Service
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={electricianPanelWork} 
                  alt="Licensed electrician working on electrical panel installation in Ontario"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Comprehensive Electrical Services
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Our licensed electricians provide complete electrical solutions for residential, commercial, and industrial clients across Ontario.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {electricalServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.services.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Featured Electrical Projects
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Explore our portfolio of electrical work across Ontario
              </p>
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={electricianImage} 
                    alt="Ontario electrician handling electrical wiring installation"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Electrical Installation</h3>
                  <p className="text-muted-foreground">
                    New electrical systems and wiring installations
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={electricianLighting} 
                    alt="Ontario electrician installing modern LED lighting fixtures"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Lighting Solutions</h3>
                  <p className="text-muted-foreground">
                    LED lighting and smart lighting system installations
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={commercialElectrical} 
                    alt="Commercial electrician installing industrial electrical systems in Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Commercial Work</h3>
                  <p className="text-muted-foreground">
                    Industrial and commercial electrical installations
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={emergencyElectrical} 
                    alt="Emergency electrical repair work by Ontario electrician"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Emergency Services</h3>
                  <p className="text-muted-foreground">
                    24/7 emergency electrical repairs and troubleshooting
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Emergency Services CTA */}
        <section className="py-16 bg-destructive text-white">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-4xl mx-auto">
              <AlertTriangle className="h-16 w-16 mx-auto mb-6" />
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Electrical Emergency? Call Now!
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Power outage? Electrical fault? Sparking outlet? Don't wait - call our emergency electricians for immediate assistance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button size="lg" variant="secondary">
                  <Phone className="h-5 w-5 mr-2" />
                  Call Emergency Line
                </Button>
                <div className="flex items-center gap-2 text-white/90">
                  <Clock className="h-5 w-5" />
                  <span>Available 24/7</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Why Choose Blue Knight Electrical?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Licensed & Insured</h3>
                <p className="text-muted-foreground">
                  ESA licensed electricians with full insurance coverage
                </p>
              </Card>

              <Card className="text-center p-6">
                <Award className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Quality Guarantee</h3>
                <p className="text-muted-foreground">
                  Warranty on all electrical work and materials
                </p>
              </Card>

              <Card className="text-center p-6">
                <Clock className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">24/7 Emergency</h3>
                <p className="text-muted-foreground">
                  Round-the-clock emergency electrical services
                </p>
              </Card>

              <Card className="text-center p-6">
                <Star className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Ontario Wide</h3>
                <p className="text-muted-foreground">
                  Serving communities across Ontario province
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Electrical Services Across Ontario
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Blue Knight Contracting provides licensed electrical services throughout Ontario, including the GTA and Niagara region
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceAreas.map((area, index) => {
                const isRegionHeader = area.includes("Ontario") || area.includes("Region") || area.includes("Area");
                
                return (
                  <Card key={index} className={`p-4 text-center ${isRegionHeader ? 'bg-primary text-white col-span-full sm:col-span-2 lg:col-span-4' : ''}`}>
                    <MapPin className={`h-6 w-6 mx-auto mb-2 ${isRegionHeader ? 'text-white' : 'text-primary'}`} />
                    <p className={`font-medium ${isRegionHeader ? 'text-white text-lg' : ''}`}>{area}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Common questions about our electrical services in Ontario
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {faqData.map((faq, index) => (
                <Card key={index} className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-primary">
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Need Licensed Electrical Work?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Contact Blue Knight Contracting today for professional electrical services across Ontario. Our licensed electricians are ready to help with all your electrical needs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                <span>Call: [Insert Phone Number]</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <span>Email: [Insert Email]</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Serving all of Ontario</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary">
                <Link to="/contact">Get Free Electrical Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                <Phone className="h-5 w-5 mr-2" />
                Emergency Service
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default ElectricianServices;